/**
 * Icon Resolution Utilities
 * Resolves string icon names to Lucide React components
 *
 * This module provides:
 * - Dynamic icon resolution from string names
 * - Icon name normalization (kebab-case, PascalCase)
 * - Caching for performance
 * - Icon name listing for admin UI
 */

import type { LucideIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { customIcons } from '@/components/icons/MultiLineChart';

// ============================================================================
// Icon Cache
// ============================================================================

/** Cache for resolved icons to avoid repeated lookups */
const iconCache = new Map<string, LucideIcon | null>();

/** Cache for normalized icon names */
const normalizedNameCache = new Map<string, string>();

// ============================================================================
// Name Normalization
// ============================================================================

/**
 * Convert kebab-case to PascalCase
 * @example kebabToPascal('arrow-left') => 'ArrowLeft'
 */
function kebabToPascal(str: string): string {
  return str
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');
}

/**
 * Convert camelCase to PascalCase
 * @example camelToPascal('arrowLeft') => 'ArrowLeft'
 */
function camelToPascal(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Convert snake_case to PascalCase
 * @example snakeToPascal('arrow_left') => 'ArrowLeft'
 */
function snakeToPascal(str: string): string {
  return str
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');
}

/**
 * Normalize icon name to PascalCase (Lucide component name format)
 * Handles: kebab-case, camelCase, snake_case, PascalCase
 */
function normalizeIconName(name: string): string {
  // Check cache first
  const cached = normalizedNameCache.get(name);
  if (cached) return cached;

  let normalized: string;

  // Detect format and convert
  if (name.includes('-')) {
    // kebab-case
    normalized = kebabToPascal(name);
  } else if (name.includes('_')) {
    // snake_case
    normalized = snakeToPascal(name);
  } else if (name[0] === name[0].toLowerCase()) {
    // camelCase
    normalized = camelToPascal(name);
  } else {
    // Already PascalCase
    normalized = name;
  }

  // Cache and return
  normalizedNameCache.set(name, normalized);
  return normalized;
}

// ============================================================================
// Icon Resolution
// ============================================================================

/**
 * Resolve a string icon name to a Lucide React component
 *
 * @param iconName - Icon name in any format (kebab-case, camelCase, PascalCase, snake_case)
 * @returns Lucide icon component or null if not found
 *
 * @example
 * resolveIcon('arrow-left')  // => ArrowLeft component
 * resolveIcon('ArrowLeft')   // => ArrowLeft component
 * resolveIcon('arrowLeft')   // => ArrowLeft component
 * resolveIcon('invalid')     // => null
 */
export function resolveIcon(iconName: string | null | undefined): LucideIcon | null {
  // Handle null/undefined/empty
  if (!iconName) return null;

  // Check cache first
  const cached = iconCache.get(iconName);
  if (cached !== undefined) return cached;

  // Normalize name to PascalCase
  const normalizedName = normalizeIconName(iconName);

  // Check custom icons first, then fall back to Lucide
  const icon =
    (customIcons as Record<string, unknown>)[normalizedName] ??
    (LucideIcons as Record<string, unknown>)[normalizedName];

  // Validate it's actually a component (object with $$typeof for React components)
  // Lucide icons are React.ForwardRefExoticComponent, which are objects, not functions
  const isReactComponent = icon !== null && typeof icon === 'object' && '$$typeof' in (icon as object);
  const result = isReactComponent ? (icon as LucideIcon) : null;

  // Cache and return
  iconCache.set(iconName, result);
  return result;
}

/**
 * Check if an icon name is valid (exists in Lucide)
 */
export function isValidIconName(iconName: string | null | undefined): boolean {
  return resolveIcon(iconName) !== null;
}

// ============================================================================
// Icon Listing (for Admin UI)
// ============================================================================

/** Cached list of available icon names */
let availableIconsCache: string[] | null = null;

/**
 * Get list of all available Lucide icon names
 * Returns names in PascalCase format
 *
 * @returns Array of icon names
 */
export function getAvailableIcons(): string[] {
  if (availableIconsCache) return availableIconsCache;

  // Filter Lucide exports to only include icon components
  // Icons are PascalCase React components, exclude utilities and types
  const iconNames = Object.keys(LucideIcons).filter((key) => {
    // Skip non-PascalCase names (utilities, types)
    if (key[0] !== key[0].toUpperCase()) return false;

    // Skip known non-icon exports and "Icon" suffix duplicates
    if (key === 'createLucideIcon' || key === 'Icon' || key.endsWith('Icon')) return false;

    // Skip "Lucide" prefixed duplicates
    if (key.startsWith('Lucide')) return false;

    // Check if it's a React component (object with $$typeof)
    const value = (LucideIcons as Record<string, unknown>)[key];
    return value !== null && typeof value === 'object' && '$$typeof' in (value as object);
  });

  availableIconsCache = iconNames.sort();
  return availableIconsCache;
}

/**
 * Search available icons by partial name
 *
 * @param query - Search query
 * @param limit - Maximum number of results (default: 50)
 * @returns Array of matching icon names
 */
export function searchIcons(query: string, limit = 50): string[] {
  const lowerQuery = query.toLowerCase();
  const allIcons = getAvailableIcons();

  return allIcons
    .filter((name) => name.toLowerCase().includes(lowerQuery))
    .slice(0, limit);
}

/**
 * Get commonly used icons (for quick selection in admin UI)
 */
export function getCommonIcons(): string[] {
  return [
    // Navigation
    'Home',
    'Menu',
    'ChevronLeft',
    'ChevronRight',
    'ChevronUp',
    'ChevronDown',
    'ArrowLeft',
    'ArrowRight',

    // Actions
    'Plus',
    'Minus',
    'X',
    'Check',
    'Edit',
    'Trash2',
    'Save',
    'Download',
    'Upload',
    'Share',
    'Copy',

    // Communication
    'MessageSquare',
    'Mail',
    'Phone',
    'Video',
    'Bell',
    'Send',

    // People
    'User',
    'Users',
    'UserPlus',
    'UserCheck',

    // Content
    'File',
    'FileText',
    'Folder',
    'FolderOpen',
    'Image',
    'Film',

    // UI Elements
    'Settings',
    'Cog',
    'Search',
    'Filter',
    'SortAsc',
    'SortDesc',

    // Status
    'AlertCircle',
    'AlertTriangle',
    'Info',
    'HelpCircle',
    'CheckCircle',
    'XCircle',

    // Security
    'Shield',
    'Lock',
    'Unlock',
    'Key',
    'Eye',
    'EyeOff',

    // Business
    'Building',
    'Briefcase',
    'Calendar',
    'Clock',
    'DollarSign',
    'CreditCard',

    // Technology
    'Laptop',
    'Monitor',
    'Smartphone',
    'Wifi',
    'Globe',
    'Database',

    // Charts
    'BarChart',
    'LineChart',
    'PieChart',
    'Activity',
    'TrendingUp',
    'TrendingDown',

    // Maps
    'Map',
    'MapPin',
    'Navigation',
    'Compass',

    // Misc
    'Star',
    'Heart',
    'Bookmark',
    'Tag',
    'Layers',
    'Grid',
    'List',
  ];
}

// ============================================================================
// Icon Categories (for organized admin UI)
// ============================================================================

/**
 * Icon categories for organized selection
 */
export interface IconCategory {
  name: string;
  icons: string[];
}

/**
 * Get icons organized by category
 */
export function getIconCategories(): IconCategory[] {
  return [
    {
      name: 'Navigation',
      icons: [
        'Home',
        'Menu',
        'ChevronLeft',
        'ChevronRight',
        'ChevronUp',
        'ChevronDown',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'CornerUpLeft',
        'CornerUpRight',
        'ExternalLink',
      ],
    },
    {
      name: 'Actions',
      icons: [
        'Plus',
        'Minus',
        'X',
        'Check',
        'Edit',
        'Edit2',
        'Edit3',
        'Trash',
        'Trash2',
        'Save',
        'Download',
        'Upload',
        'Share',
        'Share2',
        'Copy',
        'Clipboard',
        'Refresh',
        'RefreshCw',
        'RotateCw',
        'RotateCcw',
      ],
    },
    {
      name: 'Communication',
      icons: [
        'MessageSquare',
        'MessageCircle',
        'Mail',
        'Phone',
        'PhoneCall',
        'PhoneIncoming',
        'PhoneOutgoing',
        'Video',
        'VideoOff',
        'Bell',
        'BellOff',
        'Send',
        'Inbox',
        'AtSign',
      ],
    },
    {
      name: 'People',
      icons: [
        'User',
        'Users',
        'UserPlus',
        'UserMinus',
        'UserCheck',
        'UserX',
        'Contact',
        'Contact2',
      ],
    },
    {
      name: 'Content',
      icons: [
        'File',
        'FileText',
        'FilePlus',
        'FileMinus',
        'FileCheck',
        'FileX',
        'Folder',
        'FolderOpen',
        'FolderPlus',
        'FolderMinus',
        'Image',
        'Film',
        'Music',
        'Book',
        'BookOpen',
      ],
    },
    {
      name: 'UI Elements',
      icons: [
        'Settings',
        'Settings2',
        'Cog',
        'Search',
        'Filter',
        'SortAsc',
        'SortDesc',
        'Maximize',
        'Minimize',
        'Maximize2',
        'Minimize2',
        'MoreHorizontal',
        'MoreVertical',
      ],
    },
    {
      name: 'Status',
      icons: [
        'AlertCircle',
        'AlertTriangle',
        'AlertOctagon',
        'Info',
        'HelpCircle',
        'CheckCircle',
        'CheckCircle2',
        'XCircle',
        'Loader',
        'Loader2',
      ],
    },
    {
      name: 'Security',
      icons: [
        'Shield',
        'ShieldCheck',
        'ShieldOff',
        'Lock',
        'Unlock',
        'Key',
        'Eye',
        'EyeOff',
        'Fingerprint',
      ],
    },
    {
      name: 'Business',
      icons: [
        'Building',
        'Building2',
        'Briefcase',
        'Calendar',
        'CalendarDays',
        'Clock',
        'DollarSign',
        'CreditCard',
        'Receipt',
        'Wallet',
      ],
    },
    {
      name: 'Technology',
      icons: [
        'Laptop',
        'Monitor',
        'Smartphone',
        'Tablet',
        'Wifi',
        'WifiOff',
        'Globe',
        'Globe2',
        'Database',
        'Server',
        'Cloud',
        'CloudOff',
        'Code',
        'Terminal',
      ],
    },
    {
      name: 'Charts',
      icons: [
        'BarChart',
        'BarChart2',
        'BarChart3',
        'LineChart',
        'PieChart',
        'Activity',
        'TrendingUp',
        'TrendingDown',
        'Percent',
      ],
    },
    {
      name: 'Maps',
      icons: [
        'Map',
        'MapPin',
        'Navigation',
        'Navigation2',
        'Compass',
        'Locate',
        'LocateFixed',
        'LocateOff',
      ],
    },
    {
      name: 'Misc',
      icons: [
        'Star',
        'Heart',
        'Bookmark',
        'Tag',
        'Tags',
        'Layers',
        'Grid',
        'Grid3x3',
        'List',
        'LayoutGrid',
        'LayoutList',
        'Sparkles',
        'Zap',
        'Flame',
      ],
    },
  ];
}

// ============================================================================
// Cache Management
// ============================================================================

/**
 * Clear icon resolution cache
 * Useful for development/testing
 */
export function clearIconCache(): void {
  iconCache.clear();
  normalizedNameCache.clear();
  availableIconsCache = null;
}
